import { withSchemaCurrencyCode } from '@eliadmin/core/src/api/general/schema-currency-catalog';
import { createPublicRootDataLoader } from '@eliadmin/core/src/api/public-root-data';
import { createPublicDataLoader } from '@eliadmin/core/src/api/public-data-loader';
import type {
  GenericRequest,
  ImageType,
  PaginationMeta,
  PaginationQuery,
} from '@eliadmin/core/src/api/api-types';
import { fetchData, buildAuthHeader } from '@eliadmin/core/src/api/fetcher';
import {
  buildStoreListRequest,
  storeApiResources,
  type ApiWhere,
} from '@eliadmin/core/src/api/store-list-request';
import type {
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';
import type {
  ApiKeys,
  Banner,
  Country,
  Currency,
  FileType,
  Gender,
  IconsWithTextType,
  Language,
  Region,
  Shipment,
} from '@eliadmin/core/src/api/general/types/general-types';
import type { TemplateSettings } from '@eliadmin/core/src/api/general/types/template-settings-types';
import type { Translations } from '@eliadmin/core/src/api/general/types/translation-types';
import { fetchSlugDetail } from '@eliadmin/core/src/api/slugs/slugs-api';
import { env } from '@eliadmin/core/src/env';
import type { Service } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

const loadGeneralLayout = createPublicRootDataLoader(
  (locale: string) => fetchData<GeneralLayout>({
    method: 'GET',
    locale,
    uri: '/store/general/layout',
  }),
  '/store/general/layout',
);
export const fetchGeneralLayout = ({ locale }: GenericRequest) => loadGeneralLayout(locale).then(response => withSchemaCurrencyCode(response, locale));

const loadTemplateSettings = createPublicRootDataLoader(
  (locale: string) => fetchData<TemplateSettings>({
    locale,
    uri: '/store/general/template-settings',
    method: 'GET',
  }),
  '/store/general/template-settings',
);
export const fetchTemplateSettings = ({ locale }: GenericRequest) => loadTemplateSettings(locale);

const loadTranslations = createPublicRootDataLoader(
  (locale: string) => fetchData<{ data: Translations }>({
    locale,
    uri: '/store/general/translations',
    method: 'GET',
  }),
  '/store/general/translations',
);
export const fetchTranslations = ({ locale }: GenericRequest) => loadTranslations(locale);

export async function fetchHomepage({ locale, slug }: { locale: string; slug: string }) {
  return await fetchSlugDetail({
    locale,
    slug,
  })
    .then(r => r['App\\Models\\Page'])
    .catch(() => null);
}

export const fetchGenders = async ({ locale }: GenericRequest) =>
  fetchData<{ data: Gender[] }>({
    locale,
    uri: '/store/genders',
    method: 'GET',
  });

export const fetchCountries = async ({
  locale,
  query,
}: GenericRequest<{ query?: PaginationQuery & { search?: string } }>) =>
  fetchData<{ data: Country[] }>({
    locale,
    uri: '/store/countries',
    method: 'GET',
    query: {
      results: 100,
      ...query,
    },
  });

export const fetchRegions = async ({
  locale,
  query,
}: GenericRequest<{
  query?: PaginationQuery & { search?: string; country_id?: string[]; where?: ApiWhere<typeof storeApiResources.storeRegions> };
}>) => {
  const { country_id, where, ...restQuery } = query || {};
  const countryWhere = country_id?.length ? { countryId: { in: country_id } } : undefined;

  return fetchData<{ data: Region[] }>({
    locale,
    ...buildStoreListRequest(storeApiResources.storeRegions, {
      query: restQuery,
      where: countryWhere ? (where ? { and: [where, countryWhere] } : countryWhere) : where,
    }),
  });
};

export const fetchCurrencies = async ({ locale }: GenericRequest) =>
  fetchData<{ data: Currency[] }>({
    locale,
    uri: '/store/currencies',
    method: 'GET',
  });

export const fetchShipments = async ({ locale }: GenericRequest) =>
  fetchData<{ data: Shipment[] }>({
    ...buildStoreListRequest(storeApiResources.storeShipments),
    locale,
  });

export type FetchBannersQuery = {
  where?: ApiWhere<typeof storeApiResources.storeBanners>;
  results?: number;
};

export const fetchBanners = async (
  req: GenericRequest<{
    query: FetchBannersQuery;
  }>,
) => {
  const { where, ...query } = req.query;
  return fetchData<{ data: Banner[] }>({
    locale: req.locale,
    ...buildStoreListRequest(storeApiResources.storeBanners, {
      query: { results: 40, ...query },
      where,
    }),
  });
};

const fetchLanguagesOriginal = (req: { query?: PaginationQuery & { search?: string } } = {}) =>
  fetchData<{ data: Language[] }>({
    method: 'GET',
    uri: '/store/languages',
    query: req.query,
  });
const loadLanguages = createPublicRootDataLoader(
  () => fetchLanguagesOriginal(),
  '/store/languages',
);
export const fetchLanguages: typeof fetchLanguagesOriginal = (req = {}) =>
  Object.keys(req.query ?? {}).length === 0 ? loadLanguages('') : fetchLanguagesOriginal(req);

export type FetchMenuQuery = {
  where?: ApiWhere<typeof storeApiResources.storeMenus>;
  image?: boolean;
};
const fetchMenuTransport = async (req: GenericRequest<{ query: FetchMenuQuery }>) => {
  const { where, ...query } = req.query;
  return fetchData<{ data: Menu[] }>({
    locale: req.locale,
    ...buildStoreListRequest(storeApiResources.storeMenus, { query, where }),
  });
};

const loadMenu = createPublicDataLoader(
  (locale: string, queryKey: string) => fetchMenuTransport({ locale, query: JSON.parse(queryKey) }),
  'eliadmin-navigation-menu-v1',
  { revalidate: 300, tags: ['store-menus'] },
);
export const fetchMenu = (req: GenericRequest<{ query: FetchMenuQuery }>) =>
  loadMenu(req.locale, JSON.stringify(Object.fromEntries(Object.entries(req.query ?? {}).sort(([a], [b]) => a.localeCompare(b)))));

export const fetchApiKeys = async () => {
  return fetchData<{ data: ApiKeys }>({
    method: 'GET',
    uri: '/store/api-keys',
    headers: {
      'Eliadmin-Token': env.ELIADMIN_TOKEN!,
    },
  });
};

export const uploadFile = async (
  req: GenericRequest<{ formData: FormData; authToken?: string }>,
) => {
  return fetchData<{ data: FileType }>({
    method: 'POST',
    uri: '/store/files',
    body: req.formData,
    locale: req.locale,
    headers: buildAuthHeader(req.authToken),
  });
};

// Added: secret file upload endpoint used by the contact form attachment flow
export const uploadSecretFile = async (req: GenericRequest<{ formData: FormData }>) => {
  return fetchData<{ data: FileType }>({
    method: 'POST',
    uri: '/store/files/secret',
    body: req.formData,
    locale: req.locale,
  });
};

export const downloadFileFromBlob = async (
  req: GenericRequest<{ fileId: number; authToken: string; fileName: string }>,
) => {
  const baseUrl = env.NEXT_PUBLIC_API_URL as string;
  const url = `${baseUrl}/store/customers/documents/${req.fileId}/download`;

  const headers = buildAuthHeader(req.authToken);

  return fetch(url, {
    method: 'GET',
    headers: headers,
  })
    .then(response => {
      if (response.ok) {
        return response.blob();
      }
      throw new Error('Error downloading file.');
    })
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = req.fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    })
    .catch(error => {
      // eslint-disable-next-line no-console
      console.error('There has been a problem with your fetch operation:', error);
    });
};

export const fetchIconsWithText = async (req: GenericRequest<{ query?: PaginationQuery }>) => {
  return fetchData<{ data: IconsWithTextType[] }>({
    method: 'GET',
    uri: '/store/icons',
    locale: req.locale,
    query: req.query,
  });
};

export type Store = {
  id: number;
  is_current: boolean;
  language: {
    image: ImageType;
    is_default: boolean;
    locale: string;
    name: string;
  };
  name: string;
  url: string;
};

export type StoreGallery = {
  id: number;
  name: string;
  slug: string;
  description: string;
  image: ImageType;
  images: ImageType[] | null;
};

export type StoreReview = {
  id: number;
  name: string;
  review: string;
  review_type: {
    id: number;
    name: string;
    type: string;
  };
  rating: string;
};

export type ReviewTypeInfo = {
  id: number;
  name: string;
  type: string;
};

export type Review = {
  id: number;
  name: string;
  review: string;
  review_type: ReviewTypeInfo;
  rating: string;
};

export const fetchStores = async (req: GenericRequest) => {
  return fetchData<{ data: Store[] }>({
    method: 'GET',
    uri: '/store/general/stores',
    locale: req.locale,
  });
};

export type TeamMember = {
  id: number;
  name: string;
  surname: string;
  description: string;
  role: string;
  phone: string;
  email: string | null;
  image: {
    id: number;
    name: string;
    url: string;
    description: string | null;
    custom_title: string;
    metadata: unknown | null;
    is_ai_generated: boolean;
  } | null;
  languages: unknown[];
  created_at: string;
};

// Overridden: added optional query.results so callers can control page size
export const fetchTeamMembers = async <TeamMember>({
  locale,
  query,
}: GenericRequest<{ query?: { results?: number } }>) => {
  return fetchData<{ data: TeamMember[] }>({
    method: 'GET',
    uri: '/store/team-members',
    locale,
    query,
  });
};

export type StoreReviewsWhere = ApiWhere<typeof storeApiResources.storeReviews>;

export type FetchStoreReviewsRequest = GenericRequest<{
  query?: PaginationQuery;
  /** Optional selection; omit to retain all reviews and the existing aggregate. */
  where?: StoreReviewsWhere;
}>;

export type StoreReviewsResponse<TReview = StoreReview> = {
  data: TReview[];
  /** Backend average of this filtered selection, not a global store average. */
  average_review?: string | null;
};

export const fetchStoreReviews = async <TReview = StoreReview>(req: FetchStoreReviewsRequest) => {
  return fetchData<StoreReviewsResponse<TReview>>({
    ...buildStoreListRequest(storeApiResources.storeReviews, {
      query: req.query,
      where: req.where,
    }),
    locale: req.locale,
  });
};

export const fetchStoreReviewsPaginated = async <TReview = Review>(
  req: FetchStoreReviewsRequest,
) => {
  return fetchData<StoreReviewsResponse<TReview> & { meta: PaginationMeta }>({
    ...buildStoreListRequest(storeApiResources.storeReviews, {
      query: req.query,
      where: req.where,
    }),
    locale: req.locale,
  });
};

type FetchStoreGalleriesRequest = GenericRequest<{
  query?: PaginationQuery & {
    images?: boolean;
  };
}>;

export const fetchStoreGalleries = async <TGallery = StoreGallery>(
  req: FetchStoreGalleriesRequest,
) => {
  return fetchData<{ data: TGallery[] }>({
    ...buildStoreListRequest(storeApiResources.storeGalleries, { query: req.query }),
    locale: req.locale,
  });
};

export const checkVies = async ({ locale, term }: GenericRequest<{ term: string }>) => {
  return fetchData<{ valid: boolean }>({
    method: 'POST',
    uri: '/store/vies',
    body: {
      term,
    },
    locale,
  });
};

type fetchServicesQuery = {
  images?: boolean;
  results?: number;
  description?: boolean;
};

export const fetchServices = async ({
  locale,
  query,
}: GenericRequest<{ query?: fetchServicesQuery }>) =>
  fetchData<{ data: Service[] }>({
    ...buildStoreListRequest(storeApiResources.storeServices, { query }),
    locale,
  });

export type Career = {
  id: number;
  name: string;
  slug: string;
  short_description: string;
  salary: string;
  date_start: string;
  employment_type: string;
  employment_position: string;
  country: string;
  image: ImageType;
};

export const fetchCareers = async ({ locale }: GenericRequest) =>
  fetchData<{ data: Career[] }>({
    ...buildStoreListRequest(storeApiResources.storeCareers),
    locale,
  });
